/**
 * Página /forgot-password — solicita un email de recuperación de contraseña.
 *
 * Flujo:
 * 1. Usuario ingresa email institucional.
 * 2. Submit → `supabase.auth.resetPasswordForEmail()` con redirectTo
 *    apuntando a `/reset-password` (la página que captura el token).
 * 3. Mostramos estado de éxito SIEMPRE — no revelamos si el email
 *    existe o no en la base de datos (anti account enumeration).
 *    Supabase tampoco rebota explícitamente — el email solo llega
 *    si el user existe.
 *
 * Capa de seguridad: Cloudflare Turnstile invisible (R-SEC-014).
 * Sin captcha, este endpoint sería un vector para hacer mailbomb
 * a cualquier email (o enumerar emails registrados con timing).
 */
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { type FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { supabase } from "@/lib/supabase";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setErrorMsg("Espera a que termine la verificación de seguridad antes de continuar.");
      return;
    }

    setIsSubmitting(true);

    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
      ...(captchaToken ? { captchaToken } : {}),
    });

    setIsSubmitting(false);

    if (error) {
      // Errores reales (network, captcha inválido, rate limit). Account
      // enumeration está protegido por Supabase — no diferencia "user
      // no existe" de "email enviado".
      setErrorMsg(humanizeError(error.message));
      turnstileRef.current?.reset();
      setCaptchaToken(null);
      return;
    }

    setSent(true);
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface p-6">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="mb-6 inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-brand-700"
        >
          <span aria-hidden="true">←</span>
          <span>Volver a Ingresar</span>
        </Link>

        <header className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Kaptar</h1>
            <p className="text-xs text-slate-500">
              La forma inteligente de captar recursos públicos
            </p>
          </div>
        </header>

        {sent ? (
          <section
            className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6"
            aria-live="polite"
          >
            <h2 className="mb-2 text-lg font-bold text-emerald-900">Revisa tu correo</h2>
            <p className="text-sm text-emerald-800">
              Si <strong>{email}</strong> tiene una cuenta activa, te enviamos un enlace para
              restablecer tu contraseña. El link expira en 1 hora.
            </p>
            <p className="mt-4 text-xs text-emerald-700">
              ¿No te llegó? Revisa la carpeta de spam o escribe a{" "}
              <a
                href="mailto:soporte@kaptar.com.co"
                className="font-medium underline hover:no-underline"
              >
                soporte@kaptar.com.co
              </a>
              .
            </p>
          </section>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs"
            aria-labelledby="forgot-title"
          >
            <h2 id="forgot-title" className="mb-1 text-lg font-bold text-slate-900">
              Recuperar contraseña
            </h2>
            <p className="mb-6 text-sm text-slate-600">
              Ingresa el correo con el que te registraste y te enviamos un enlace para crear una
              nueva contraseña.
            </p>

            <label className="mb-5 block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Correo institucional
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600 focus:outline-hidden"
                placeholder="planeacion@tumunicipio.gov.co"
              />
            </label>

            {TURNSTILE_SITE_KEY && (
              <div className="mb-4 flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  options={{
                    theme: "light",
                    size: "flexible",
                  }}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onError={() => setCaptchaToken(null)}
                  onExpire={() => setCaptchaToken(null)}
                />
              </div>
            )}

            {errorMsg && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              >
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-md bg-cta-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "Enviando…" : "Enviar enlace de recuperación"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}

function humanizeError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("rate limit")) {
    return "Demasiados intentos. Espera unos minutos antes de reintentar.";
  }
  if (m.includes("captcha")) {
    return "La verificación de seguridad falló. Recarga la página e intenta de nuevo.";
  }
  return "No pudimos procesar la solicitud. Intenta de nuevo en unos minutos.";
}
